import { Directive, effect, forwardRef, inject, Injectable, input } from '@angular/core';
import { MAT_DATE_FORMATS, MatDateFormats, NativeDateAdapter } from '@angular/material/core';
import { LanguageService } from 'src/app/core/services/language.service';

@Injectable()
export class EmpDateAdapter extends NativeDateAdapter {
  private languageService = inject(LanguageService);

  constructor() {
    super();
    effect(() => super.setLocale(this.languageService.locale()));
  }

  public override format(date: Date, format: Intl.DateTimeFormatOptions): string {
    const formatter = new Intl.DateTimeFormat(this.locale, format);
    return formatter.format(date);
  }

  public override parse(value: string): Date | null {
    const dateRegex = /^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/;
    const match = value.match(dateRegex);

    if (match) {
      const [, dayString, monthString, yearString] = match;
      const day = parseInt(dayString);
      const month = parseInt(monthString);
      const year = parseInt(yearString);

      // 30-02-2000 is valid input for new Date(), it returns 01-03-2000
      // different month = reject input
      const date = new Date(year, month - 1, day);
      return date.getMonth() === month - 1 ? date : null;
    } else {
      return null;
    }
  }

  public override getFirstDayOfWeek(): number {
    return 1;
  }
}

export const DEFAULT_DATE_FORMATS = {
  parse: {
    dateInput: null
  },
  display: {
    dateInput: {
      dateStyle: 'short'
    },
    monthYearLabel: {
      month: 'numeric',
      year: 'numeric'
    },
    dateA11yLabel: {
      dateStyle: 'short'
    },
    monthYearA11yLabel: {
      dateStyle: 'short'
    },
    monthLabel: {
      month: 'long'
    }
  }
} as const satisfies DateFormatsIntlOptions & MatDateFormats;

type DateFormatsIntlOptions = {
  parse: {
    dateInput: null;
  };
  display: {
    dateInput: Intl.DateTimeFormatOptions;
    monthYearLabel: Intl.DateTimeFormatOptions;
    dateA11yLabel: Intl.DateTimeFormatOptions;
    monthYearA11yLabel: Intl.DateTimeFormatOptions;
    monthLabel: Intl.DateTimeFormatOptions;
  };
};

export const YEAR_FORMATS: DateFormatsIntlOptions = {
  parse: {
    dateInput: null
  },
  display: {
    dateInput: {
      year: 'numeric'
    },
    monthYearLabel: {
      year: 'numeric'
    },
    dateA11yLabel: {
      year: 'numeric'
    },
    monthYearA11yLabel: {
      year: 'numeric'
    },
    monthLabel: {
      year: 'numeric'
    }
  }
} as const satisfies DateFormatsIntlOptions satisfies MatDateFormats;

@Directive({
  selector: '[empDynamicDatepicker]',
  providers: [
    {
      provide: MAT_DATE_FORMATS,
      useExisting: forwardRef(() => DynamicDatepickerDirective)
    }
  ]
})
export class DynamicDatepickerDirective {
  format = input<DateFormatsIntlOptions & MatDateFormats>(DEFAULT_DATE_FORMATS);

  public parse = DEFAULT_DATE_FORMATS.parse;

  get display() {
    return this.format().display;
  }
}
